import { createAdminV2Error } from '@/admin-v2/foundation/errors';
import {
  type AdminV2EffectiveImageInputConstraints,
  mergeAdminV2EffectiveImageInputConstraints,
} from '@/admin-v2/runtime/image-input-constraints';
import {
  type AdminV2CreditCalculationMode,
  resolveAdminV2CreditCalculationMode,
} from '@/admin-v2/tools/credit-calculation-mode';
import { createAdminV2ParameterCalculatorInstanceSchema } from '@/admin-v2/tools/parameter-calculator';
import {
  accessPolicyRuleSchema,
  adminV2CreditFactorSchema,
  adminV2PaidAccessDiscountSchema,
  adminV2ToolFieldSchema,
} from '@/admin-v2/tools/tool-contract';
import {
  getAdminV2UserFacingToolFieldDefaultValue,
  getAdminV2UserFacingToolFieldValue,
  isAdminV2UserFacingToolField,
  toAdminV2UserFacingToolField,
} from '@/admin-v2/tools/tool-field-visibility';
import type { AdminV2ToolDisplayIcon } from '@/admin-v2/tools/tool-icon';
import {
  AdminV2ToolLocalizedCopyError,
  resolveAdminV2ToolDisplay,
} from '@/admin-v2/tools/tool-localized-copy';
import type { AdminV2ToolRuntimePackage } from '@/admin-v2/tools/tool-types';
import { getStoragePublicUrl } from '@/lib/urls/urls';
import { isWorkspaceStaticIcon } from '@/workspace/static-assets';
import { z } from 'zod';
import { adminV2ResolvedWorkspaceActionBindingSchema } from './workspace-action-slots';
import { createAdminV2WorkspaceInputLabelsSchema } from './workspace-input-labels';
import { adminV2ResolvedWorkspaceToolbarItemSchema } from './workspace-toolbar';

export type AdminV2WorkspaceToolSummary = {
  toolId: string;
  surfaceMode: AdminV2ToolRuntimePackage['workspaceView']['surfaceMode'];
  name: string;
  badge: string | null;
  icon: AdminV2PublicWorkspaceToolIcon | null;
  summary: string | null;
  fieldCount: number;
  acceptsPrimaryImage: boolean;
  accessPolicy: z.infer<typeof accessPolicyRuleSchema>;
  requiredPaid: boolean;
  paidAccessDiscount?: z.infer<typeof adminV2PaidAccessDiscountSchema>;
};

export type AdminV2WorkspaceToolCatalogProjection = {
  schemaVersion: 1;
  toolId: string;
  toolVersionId: string;
  locale: string;
  summary: AdminV2WorkspaceToolSummary | null;
};

export type AdminV2WorkspaceOutputBindings = {
  primaryMediaSlotId: string;
  /** Image runtimes also expose the historical binding during migration. */
  primaryImageSlotId?: string;
  mediaKind: 'image' | 'video';
  slots: Array<{
    id: string;
    type: 'string' | 'number' | 'boolean' | 'json';
    repeated: boolean;
  }>;
};

export type AdminV2WorkspaceStartupContext = {
  action: 'switch_to_workspace';
  primaryImage?: string;
  autoSubmit?: boolean;
  defaults?: Record<string, unknown>;
};

export type AdminV2WorkspaceStartup = {
  toolId: string;
  input: Record<string, unknown>;
  autoSubmit: boolean;
  missingRequiredFieldIds: string[];
};

export type AdminV2PublicWorkspaceToolIcon =
  | string
  | {
      iconSource: 'custom';
      url: string;
    };

type AdminV2PublicWorkspaceToolDisplay = Omit<
  AdminV2ToolRuntimePackage['display'],
  'icon'
> & {
  icon: AdminV2PublicWorkspaceToolIcon | null;
};

/**
 * Browser input validation needs aggregate limits, never Model identities,
 * Provider details, or the private execution graph.
 */
export type AdminV2PublicWorkspaceClientInputLimits = {
  maxPromptCharacters: number;
  imageConstraints: AdminV2EffectiveImageInputConstraints;
  imageConstraintRules?: Array<{
    activeWhen: { fieldId: string; values: Array<string | number | boolean> };
    imageConstraints: AdminV2EffectiveImageInputConstraints;
  }>;
};

export type AdminV2PublicWorkspaceTemplatePicker = {
  groups: Array<{
    id: string;
    required: boolean;
    selectionFieldId: string;
    customImageFieldId?: string;
    templates: Array<{
      value: string;
      previewUrl: string;
      width: number;
      height: number;
      animatedPreviewUrl?: string;
      animatedWidth?: number;
      animatedHeight?: number;
    }>;
  }>;
};

export type AdminV2PublicWorkspaceToolRuntimePackage = Pick<
  AdminV2ToolRuntimePackage,
  | 'packageKind'
  | 'schemaVersion'
  | 'generatedAt'
  | 'toolId'
  | 'toolVersionId'
  | 'versionNumber'
  | 'runtimeType'
  | 'availableSurfaces'
  | 'inputContract'
  | 'outputContract'
  | 'toolFields'
  | 'userCreditPolicy'
  | 'accessPolicy'
> & {
  display: AdminV2PublicWorkspaceToolDisplay;
  clientInputLimits: AdminV2PublicWorkspaceClientInputLimits;
  creditCalculationMode: AdminV2CreditCalculationMode;
  workspaceView: Omit<
    AdminV2ToolRuntimePackage['workspaceView'],
    'routeTemplatePicker' | 'templatePicker'
  > & {
    templatePicker?: AdminV2PublicWorkspaceTemplatePicker;
  };
};

/*
 * Keep the raw Tool icon reference server-owned. Public Workspace responses
 * expose only the app key or a resolved custom SVG URL.
 */
export function toAdminV2PublicWorkspaceToolIcon(
  icon: AdminV2ToolDisplayIcon | null | undefined
): AdminV2PublicWorkspaceToolIcon | null {
  if (!icon) return null;
  if (typeof icon === 'string') return icon;
  return {
    iconSource: 'custom',
    url: 'url' in icon ? icon.url : getStoragePublicUrl(icon.storageKey),
  };
}

const adminV2PublicWorkspaceToolIconSchema = z
  .union([
    z.string().min(1),
    z
      .object({
        iconSource: z.literal('custom'),
        url: z
          .string()
          .url()
          .regex(/^https?:\/\//iu, 'Custom Tool icon URL must use HTTP(S).')
          .or(
            z.string().refine(isWorkspaceStaticIcon, 'Unknown local Tool icon.')
          ),
      })
      .strict(),
  ])
  .nullable();

export const adminV2WorkspaceToolSummarySchema = z
  .object({
    toolId: z.string().min(1),
    surfaceMode: z.enum(['canvas', 'conversation']),
    name: z.string().min(1),
    badge: z.string().min(1).nullable(),
    icon: adminV2PublicWorkspaceToolIconSchema,
    summary: z.string().min(1).nullable(),
    fieldCount: z.number().int().nonnegative(),
    acceptsPrimaryImage: z.boolean(),
    accessPolicy: accessPolicyRuleSchema,
    requiredPaid: z.boolean(),
    paidAccessDiscount: adminV2PaidAccessDiscountSchema.optional(),
  })
  .strict();

export const adminV2WorkspaceToolCatalogProjectionSchema = z
  .object({
    schemaVersion: z.literal(1),
    toolId: z.string().min(1),
    toolVersionId: z.string().min(1),
    locale: z.string().min(1),
    summary: adminV2WorkspaceToolSummarySchema.nullable(),
  })
  .strict();

const adminV2PublicWorkspaceOutputSlotSchema = z
  .object({
    id: z.string().min(1),
    type: z.enum(['string', 'number', 'boolean', 'json']),
    repeated: z.boolean(),
  })
  .strict();

function createAdminV2PublicWorkspaceToolFieldSchema(
  localizedTextSchema: z.ZodType<Record<string, string>>
) {
  return adminV2ToolFieldSchema.extend({
    label: localizedTextSchema,
    placeholder: localizedTextSchema.optional(),
    tooltip: localizedTextSchema.optional(),
    inputRole: adminV2ToolFieldSchema.shape.inputRole.optional(),
    prefillable: z.boolean().optional(),
    adminOnly: z.boolean().optional(),
    userVisible: z.boolean().optional(),
    options: z.array(
      adminV2ToolFieldSchema.shape.options.element.extend({
        label: localizedTextSchema,
        labelOverride:
          adminV2ToolFieldSchema.shape.options.element.shape.labelOverride
            .unwrap()
            .extend({ label: localizedTextSchema })
            .optional(),
        description: localizedTextSchema.optional(),
        hidden: z.boolean().optional(),
        disabled: z.boolean().optional(),
      })
    ),
  });
}

function createAdminV2PublicWorkspaceToolRuntimePackageSchema(
  localizedTextSchema: z.ZodType<Record<string, string>>,
  containerLocalizedTextSchema: z.ZodType<Record<string, string>> = z.record(
    z.string(),
    z.string()
  )
): z.ZodType<AdminV2PublicWorkspaceToolRuntimePackage> {
  const toolFieldSchema =
    createAdminV2PublicWorkspaceToolFieldSchema(localizedTextSchema);

  return z
    .object({
      packageKind: z.literal('tool'),
      schemaVersion: z.literal(1),
      generatedAt: z.string().min(1),
      toolId: z.string().min(1),
      toolVersionId: z.string().min(1),
      versionNumber: z.number().int().min(1),
      runtimeType: z.enum(['aiJob', 'clientOnly']),
      availableSurfaces: z.array(z.enum(['workspace_web', 'extension'])),
      display: z
        .object({
          name: z.string().min(1),
          badge: z.string().min(1).nullable(),
          icon: adminV2PublicWorkspaceToolIconSchema,
          summary: z.string().min(1).nullable(),
          localized: z
            .object({
              name: containerLocalizedTextSchema.optional(),
              badge: containerLocalizedTextSchema.optional(),
              summary: containerLocalizedTextSchema.optional(),
            })
            .strict()
            .optional(),
        })
        .strict(),
      inputContract: z.object({ fields: z.array(toolFieldSchema) }).strict(),
      outputContract: z
        .object({
          kind: z.enum(['image', 'image_array', 'video', 'text', 'json']),
          maxOutputs: z.number().int().min(1),
          slots: z.array(adminV2PublicWorkspaceOutputSlotSchema).min(1),
          outputType: z.enum(['image', 'imageList', 'file', 'text', 'video']),
          outputNameSuffix: z.string().min(1),
          primaryImageSlotId: z.string().min(1).optional(),
          transparentBackground: z.boolean().optional(),
        })
        .strict(),
      toolFields: z.array(toolFieldSchema),
      creditCalculationMode: z.enum(['static', 'server_quote']),
      clientInputLimits: z
        .object({
          maxPromptCharacters: z.number().int().min(0),
          imageConstraints: z
            .object({
              minWidth: z.number().int().positive(),
              minHeight: z.number().int().positive(),
              freeMaxBytes: z.number().int().positive(),
              paidMaxBytes: z.number().int().positive(),
              allowedMimeTypes: z.array(z.string().min(1)).optional(),
              maxWidth: z.number().int().positive().optional(),
              maxHeight: z.number().int().positive().optional(),
              maxMegapixels: z.number().positive().optional(),
              maxTotalBytes: z.number().int().positive().optional(),
              colorModes: z.array(z.string().min(1)).optional(),
              allowedAspectRatios: z.array(z.string().min(1)).optional(),
            })
            .strict(),
          imageConstraintRules: z
            .array(
              z
                .object({
                  activeWhen: z
                    .object({
                      fieldId: z.string().min(1),
                      values: z
                        .array(z.union([z.string(), z.number(), z.boolean()]))
                        .min(1),
                    })
                    .strict(),
                  imageConstraints: z
                    .object({
                      minWidth: z.number().int().positive(),
                      minHeight: z.number().int().positive(),
                      freeMaxBytes: z.number().int().positive(),
                      paidMaxBytes: z.number().int().positive(),
                      allowedMimeTypes: z.array(z.string().min(1)).optional(),
                      maxWidth: z.number().int().positive().optional(),
                      maxHeight: z.number().int().positive().optional(),
                      maxMegapixels: z.number().positive().optional(),
                      maxTotalBytes: z.number().int().positive().optional(),
                      colorModes: z.array(z.string().min(1)).optional(),
                      allowedAspectRatios: z
                        .array(z.string().min(1))
                        .optional(),
                    })
                    .strict(),
                })
                .strict()
            )
            .optional(),
        })
        .strict(),
      userCreditPolicy: z
        .object({
          paidAccessDiscount: adminV2PaidAccessDiscountSchema.optional(),
          terms: z.array(
            z
              .object({
                factors: z.array(adminV2CreditFactorSchema).min(1),
              })
              .strict()
          ),
        })
        .strict(),
      accessPolicy: z
        .object({
          tool: accessPolicyRuleSchema,
          branchPolicies: z.array(
            z
              .object({
                nodeId: z.string().min(1),
                policy: accessPolicyRuleSchema,
              })
              .strict()
          ),
        })
        .strict(),
      workspaceView: z
        .object({
          enabled: z.boolean(),
          surfaceMode: z.enum(['canvas', 'conversation']),
          layoutVariant: z.enum([
            'classic',
            'primary-with-reference',
            'composer',
          ]),
          showcase: z
            .object({
              template: z.enum(['canvas-stage', 'prompt-demo']),
              compareEnabled: z.boolean(),
            })
            .strict()
            .nullable(),
          inputLabels: createAdminV2WorkspaceInputLabelsSchema(
            containerLocalizedTextSchema
          ).optional(),
          actionBindings: z
            .array(adminV2ResolvedWorkspaceActionBindingSchema)
            .optional(),
          toolbar: z
            .object({
              items: z.array(adminV2ResolvedWorkspaceToolbarItemSchema),
            })
            .strict()
            .optional(),
          repeatGeneration: z
            .object({
              maxJobs: z.number().int().min(2).max(4),
            })
            .strict()
            .optional(),
          parameterCalculators: z
            .array(
              createAdminV2ParameterCalculatorInstanceSchema(
                containerLocalizedTextSchema
              )
            )
            .max(1)
            .optional(),
          templatePicker: z
            .object({
              groups: z.array(
                z
                  .object({
                    id: z.string().min(1),
                    required: z.boolean(),
                    selectionFieldId: z.string().min(1),
                    customImageFieldId: z.string().min(1).optional(),
                    templates: z.array(
                      z
                        .object({
                          value: z.string().min(1),
                          previewUrl: z
                            .string()
                            .url()
                            .regex(
                              /^https?:\/\//iu,
                              'Template preview URL must use HTTP(S).'
                            ),
                          width: z.number().int().min(1),
                          height: z.number().int().min(1),
                          animatedPreviewUrl: z
                            .string()
                            .url()
                            .regex(
                              /^https?:\/\//iu,
                              'Animated template preview URL must use HTTP(S).'
                            )
                            .optional(),
                          animatedWidth: z.number().int().min(1).optional(),
                          animatedHeight: z.number().int().min(1).optional(),
                        })
                        .strict()
                        .superRefine((template, context) => {
                          const animatedFields = [
                            template.animatedPreviewUrl,
                            template.animatedWidth,
                            template.animatedHeight,
                          ];
                          if (
                            animatedFields.some(
                              (value) => value !== undefined
                            ) &&
                            animatedFields.some((value) => value === undefined)
                          ) {
                            context.addIssue({
                              code: 'custom',
                              message:
                                'Animated template preview URL and dimensions must be configured together.',
                            });
                          }
                        })
                    ),
                  })
                  .strict()
              ),
            })
            .strict()
            .optional(),
          output: z
            .object({
              outputType: z.enum([
                'image',
                'imageList',
                'file',
                'text',
                'video',
              ]),
              outputNameSuffix: z.string().min(1),
              primaryImageSlotId: z.string().min(1).optional(),
              transparentBackground: z.boolean(),
            })
            .strict(),
        })
        .strict(),
    })
    .strict() as unknown as z.ZodType<AdminV2PublicWorkspaceToolRuntimePackage>;
}

export const adminV2PublicWorkspaceToolRuntimePackageSchema =
  createAdminV2PublicWorkspaceToolRuntimePackageSchema(
    adminV2ToolFieldSchema.shape.label
  );

function createAdminV2ResolvedWorkspaceLocalizedTextSchema(locale: string) {
  return z
    .record(
      z.string().min(1),
      z.string().refine((value) => value.trim().length > 0)
    )
    .superRefine((value, context) => {
      const keys = Object.keys(value);
      if (keys.length !== 1 || keys[0] !== locale) {
        context.addIssue({
          code: 'custom',
          message: `Expected resolved text for locale ${locale}.`,
        });
      }
    });
}

export function createAdminV2PublicWorkspaceToolRuntimePackageSchemaForLocale(
  locale: string
) {
  const localizedTextSchema =
    createAdminV2ResolvedWorkspaceLocalizedTextSchema(locale);
  return createAdminV2PublicWorkspaceToolRuntimePackageSchema(
    localizedTextSchema,
    localizedTextSchema
  );
}

function hasStartupValue(value: unknown) {
  return value !== undefined && value !== null && value !== '';
}

export function isAdminV2WorkspaceUserVisibleToolField(
  field: AdminV2ToolRuntimePackage['toolFields'][number]
) {
  return isAdminV2UserFacingToolField(field);
}

export function getAdminV2WorkspaceUserVisibleToolFields(
  runtimePackage: Pick<AdminV2ToolRuntimePackage, 'toolFields'>
) {
  return runtimePackage.toolFields.filter(
    isAdminV2WorkspaceUserVisibleToolField
  );
}

export function toAdminV2PublicWorkspaceRuntimePackage(
  runtimePackage: AdminV2ToolRuntimePackage
): AdminV2PublicWorkspaceToolRuntimePackage {
  const creditCalculationMode = resolveAdminV2CreditCalculationMode(
    runtimePackage.userCreditPolicy
  );
  if (creditCalculationMode === 'server_quote') {
    throw createAdminV2Error({
      code: 'ADMIN_V2_WORKSPACE_CREDIT_POLICY_NOT_CLIENT_RESOLVABLE',
      message:
        'Workspace Tool credit policies must be fully resolvable from published Tool fields and client image metadata.',
      fieldPath: 'userCreditPolicy',
      status: 409,
    });
  }

  const visibleFields = getAdminV2WorkspaceUserVisibleToolFields(
    runtimePackage
  ).map((field) => toAdminV2UserFacingToolField(field));
  const visibleFieldById = new Map(
    visibleFields.map((field) => [field.id, field])
  );
  const visibleFieldIds = new Set(visibleFieldById.keys());
  const positivePromptLimits = runtimePackage.modelUsages
    .map((usage) => usage.runtimePackage.inputLimits.maxPromptCharacters)
    .filter((limit) => limit > 0);
  const conditionalModelUsageIds = new Set(
    (runtimePackage.workspaceView.imageInputConstraintRules ?? []).flatMap(
      (rule) => rule.modelUsageIds
    )
  );
  const imageConstraintRules = (
    runtimePackage.workspaceView.imageInputConstraintRules ?? []
  ).map((rule) => ({
    activeWhen: structuredClone(rule.activeWhen),
    imageConstraints: mergeAdminV2EffectiveImageInputConstraints(
      rule.modelUsageIds.map((usageId) => {
        const usage = runtimePackage.modelUsages.find(
          (candidate) => candidate.usageId === usageId
        );
        if (!usage) {
          throw createAdminV2Error({
            code: 'ADMIN_V2_WORKSPACE_IMAGE_CONSTRAINT_RULE_INVALID',
            message: `Workspace image constraint rule references unknown Model usage ${usageId}.`,
            fieldPath: 'workspaceView.imageInputConstraintRules',
            status: 409,
          });
        }
        return usage.runtimePackage.inputLimits.imageConstraints;
      })
    ),
  }));
  const clientInputLimits: AdminV2PublicWorkspaceClientInputLimits = {
    maxPromptCharacters:
      positivePromptLimits.length > 0 ? Math.min(...positivePromptLimits) : 0,
    imageConstraints: mergeAdminV2EffectiveImageInputConstraints(
      runtimePackage.modelUsages
        .filter((usage) => !conditionalModelUsageIds.has(usage.usageId))
        .map((usage) => usage.runtimePackage.inputLimits.imageConstraints)
    ),
    ...(imageConstraintRules.length > 0 ? { imageConstraintRules } : {}),
  };
  const promptTemplatePicker = runtimePackage.workspaceView.templatePicker
    ? {
        groups: runtimePackage.workspaceView.templatePicker.groups.map(
          (group) => {
            const field = runtimePackage.toolFields.find(
              (candidate) => candidate.id === group.selectionFieldId
            );
            const visibleOptionValues = new Set(
              (field?.options ?? []).flatMap((option) =>
                !option.hidden && typeof option.value === 'string'
                  ? [option.value]
                  : []
              )
            );
            return {
              id: group.id,
              required: group.required,
              selectionFieldId: group.selectionFieldId,
              ...(group.customImageFieldId
                ? { customImageFieldId: group.customImageFieldId }
                : {}),
              templates: group.templates
                .filter((template) => visibleOptionValues.has(template.value))
                .map((template) => ({
                  value: template.value,
                  previewUrl: getStoragePublicUrl(template.preview.storageKey),
                  width: template.preview.width,
                  height: template.preview.height,
                  ...(template.animatedPreview
                    ? {
                        animatedPreviewUrl: getStoragePublicUrl(
                          template.animatedPreview.storageKey
                        ),
                        animatedWidth: template.animatedPreview.width,
                        animatedHeight: template.animatedPreview.height,
                      }
                    : {}),
                })),
            };
          }
        ),
      }
    : undefined;
  const routeTemplatePicker = runtimePackage.workspaceView.routeTemplatePicker
    ? (() => {
        const picker = runtimePackage.workspaceView.routeTemplatePicker;
        const field = runtimePackage.toolFields.find(
          (candidate) => candidate.id === picker.selectionFieldId
        );
        const visibleOptionValues = new Set(
          (field?.options ?? []).flatMap((option) =>
            !option.hidden && typeof option.value === 'string'
              ? [option.value]
              : []
          )
        );
        return {
          groups: [
            {
              id: picker.selectionFieldId,
              required: true,
              selectionFieldId: picker.selectionFieldId,
              templates: picker.templates
                .filter((template) => visibleOptionValues.has(template.value))
                .map((template) => ({
                  value: template.value,
                  previewUrl: getStoragePublicUrl(template.preview.storageKey),
                  width: template.preview.width,
                  height: template.preview.height,
                })),
            },
          ],
        };
      })()
    : undefined;
  const templatePicker = promptTemplatePicker ?? routeTemplatePicker;

  return {
    packageKind: runtimePackage.packageKind,
    schemaVersion: runtimePackage.schemaVersion,
    generatedAt: runtimePackage.generatedAt,
    toolId: runtimePackage.toolId,
    toolVersionId: runtimePackage.toolVersionId,
    versionNumber: runtimePackage.versionNumber,
    runtimeType: runtimePackage.runtimeType,
    availableSurfaces: [...runtimePackage.availableSurfaces],
    display: {
      ...structuredClone(runtimePackage.display),
      icon: toAdminV2PublicWorkspaceToolIcon(runtimePackage.display.icon),
    },
    inputContract: {
      fields: runtimePackage.inputContract.fields
        .filter((field) => visibleFieldIds.has(field.id))
        .map((field) => toAdminV2UserFacingToolField(field)),
    },
    outputContract: structuredClone(runtimePackage.outputContract),
    toolFields: visibleFields,
    creditCalculationMode,
    clientInputLimits,
    userCreditPolicy: structuredClone(runtimePackage.userCreditPolicy),
    accessPolicy: structuredClone(runtimePackage.accessPolicy),
    workspaceView: {
      enabled: runtimePackage.workspaceView.enabled,
      surfaceMode: runtimePackage.workspaceView.surfaceMode,
      layoutVariant: runtimePackage.workspaceView.layoutVariant,
      showcase: structuredClone(runtimePackage.workspaceView.showcase),
      ...(runtimePackage.workspaceView.inputLabels
        ? {
            inputLabels: structuredClone(
              runtimePackage.workspaceView.inputLabels
            ),
          }
        : {}),
      ...(runtimePackage.workspaceView.actionBindings
        ? {
            actionBindings: structuredClone(
              runtimePackage.workspaceView.actionBindings
            ),
          }
        : {}),
      ...(runtimePackage.workspaceView.toolbar
        ? { toolbar: structuredClone(runtimePackage.workspaceView.toolbar) }
        : {}),
      ...(runtimePackage.workspaceView.repeatGeneration
        ? {
            repeatGeneration: structuredClone(
              runtimePackage.workspaceView.repeatGeneration
            ),
          }
        : {}),
      ...(runtimePackage.workspaceView.parameterCalculators?.length
        ? {
            parameterCalculators: structuredClone(
              runtimePackage.workspaceView.parameterCalculators
            ),
          }
        : {}),
      ...(templatePicker ? { templatePicker } : {}),
      output: structuredClone(runtimePackage.workspaceView.output),
    },
  };
}

export function toAdminV2PublicWorkspaceStartup(params: {
  runtimePackage: Pick<AdminV2ToolRuntimePackage, 'toolFields'>;
  startup: AdminV2WorkspaceStartup;
}): AdminV2WorkspaceStartup {
  const visibleFieldById = new Map(
    getAdminV2WorkspaceUserVisibleToolFields(params.runtimePackage).map(
      (field) => [field.id, field]
    )
  );

  return {
    toolId: params.startup.toolId,
    input: Object.fromEntries(
      Object.entries(params.startup.input).flatMap(([fieldId, value]) => {
        const field = visibleFieldById.get(fieldId);
        if (!field) {
          return [];
        }
        const visibleValue = getAdminV2UserFacingToolFieldValue({
          field,
          value,
        });
        return visibleValue === undefined ? [] : [[fieldId, visibleValue]];
      })
    ),
    autoSubmit: params.startup.autoSubmit,
    missingRequiredFieldIds: params.startup.missingRequiredFieldIds.filter(
      (fieldId) => visibleFieldById.has(fieldId)
    ),
  };
}

export function isAdminV2ToolAvailableOnSurface(
  runtimePackage: Pick<AdminV2ToolRuntimePackage, 'availableSurfaces'>,
  surface: 'workspace_web' | 'extension'
) {
  return runtimePackage.availableSurfaces.includes(surface);
}

export function createAdminV2WorkspaceToolCatalogProjection(params: {
  runtimePackage: AdminV2ToolRuntimePackage;
  locale: string;
}): AdminV2WorkspaceToolCatalogProjection {
  const { locale, runtimePackage } = params;
  const base = {
    schemaVersion: 1 as const,
    toolId: runtimePackage.toolId,
    toolVersionId: runtimePackage.toolVersionId,
    locale,
  };

  if (
    !runtimePackage.workspaceView.enabled ||
    !isAdminV2ToolAvailableOnSurface(runtimePackage, 'workspace_web')
  ) {
    return adminV2WorkspaceToolCatalogProjectionSchema.parse({
      ...base,
      summary: null,
    });
  }

  const accessPolicy = accessPolicyRuleSchema.parse(
    runtimePackage.accessPolicy.tool
  );
  let display: ReturnType<typeof resolveAdminV2ToolDisplay>;
  try {
    display = resolveAdminV2ToolDisplay({
      display: runtimePackage.display,
      locale,
      fieldPath: `workspace.tools.${runtimePackage.toolId}.display`,
    });
  } catch (error) {
    if (!(error instanceof AdminV2ToolLocalizedCopyError)) throw error;
    return adminV2WorkspaceToolCatalogProjectionSchema.parse({
      ...base,
      summary: null,
    });
  }
  const visibleFields =
    getAdminV2WorkspaceUserVisibleToolFields(runtimePackage);

  return adminV2WorkspaceToolCatalogProjectionSchema.parse({
    ...base,
    summary: {
      toolId: runtimePackage.toolId,
      surfaceMode: runtimePackage.workspaceView.surfaceMode,
      ...display,
      icon: toAdminV2PublicWorkspaceToolIcon(display.icon),
      fieldCount: visibleFields.length,
      acceptsPrimaryImage: visibleFields.some(
        (field) => field.control === 'image_file'
      ),
      accessPolicy,
      requiredPaid: accessPolicy.requiredAccess !== 'free',
      ...(runtimePackage.userCreditPolicy.paidAccessDiscount
        ? {
            paidAccessDiscount: structuredClone(
              runtimePackage.userCreditPolicy.paidAccessDiscount
            ),
          }
        : {}),
    },
  });
}

function buildAdminV2WorkspaceStartupInput(params: {
  runtimePackage: AdminV2ToolRuntimePackage;
  context: AdminV2WorkspaceStartupContext | null;
}) {
  const input: Record<string, unknown> = {};
  const contextDefaults = params.context?.defaults ?? {};

  for (const field of getAdminV2WorkspaceUserVisibleToolFields(
    params.runtimePackage
  )) {
    if (
      Object.hasOwn(contextDefaults, field.id) &&
      hasStartupValue(contextDefaults[field.id])
    ) {
      input[field.id] = contextDefaults[field.id];
      continue;
    }

    const defaultValue = getAdminV2UserFacingToolFieldDefaultValue(field);
    if (hasStartupValue(defaultValue)) {
      input[field.id] = defaultValue;
    }
  }

  return input;
}

export function resolveAdminV2WorkspaceOutputBindings(
  runtimePackage: Pick<AdminV2ToolRuntimePackage, 'outputContract'>
): AdminV2WorkspaceOutputBindings {
  const outputContract = runtimePackage.outputContract;
  if (
    outputContract.kind !== 'image' &&
    outputContract.kind !== 'image_array' &&
    outputContract.kind !== 'video'
  ) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_WORKSPACE_OUTPUT_KIND_UNSUPPORTED',
      message: 'Workspace Tool runtime must produce image or video output.',
      fieldPath: 'outputContract.kind',
      status: 409,
    });
  }

  const preferredImageSlotId =
    outputContract.kind === 'video'
      ? undefined
      : outputContract.primaryImageSlotId;
  const primaryMediaSlot =
    outputContract.slots.find((slot) => slot.id === preferredImageSlotId) ??
    outputContract.slots.find((slot) => {
      if (outputContract.kind === 'image_array') {
        return slot.type === 'string' && slot.repeated;
      }
      return slot.type === 'string' && !slot.repeated;
    });
  if (!primaryMediaSlot) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_WORKSPACE_OUTPUT_SLOT_MISSING',
      message: 'Workspace Tool runtime has no compatible media output slot.',
      fieldPath: 'outputContract.slots',
      status: 409,
    });
  }

  return {
    primaryMediaSlotId: primaryMediaSlot.id,
    ...(outputContract.kind === 'video'
      ? {}
      : { primaryImageSlotId: primaryMediaSlot.id }),
    mediaKind: outputContract.kind === 'video' ? 'video' : 'image',
    slots: outputContract.slots.map((slot) => ({
      id: slot.id,
      type: slot.type,
      repeated: slot.repeated,
    })),
  };
}

export function resolveAdminV2WorkspaceStartup(params: {
  runtimePackage: AdminV2ToolRuntimePackage;
  context: AdminV2WorkspaceStartupContext | null;
}): AdminV2WorkspaceStartup {
  const input = buildAdminV2WorkspaceStartupInput(params);
  const visibleFields = getAdminV2WorkspaceUserVisibleToolFields(
    params.runtimePackage
  );
  const imageFields = visibleFields.filter(
    (field) => field.control === 'image_file'
  );
  const primaryImageField =
    imageFields.find((field) => field.inputRole === 'primaryImage') ??
    imageFields[0];

  if (params.context?.primaryImage && primaryImageField) {
    input[primaryImageField.id] = params.context.primaryImage;
  }

  const missingRequired = visibleFields.filter(
    (field) => field.required && !hasStartupValue(input[field.id])
  );

  return {
    toolId: params.runtimePackage.toolId,
    input,
    autoSubmit:
      params.context?.autoSubmit === true && missingRequired.length === 0,
    missingRequiredFieldIds: missingRequired.map((field) => field.id),
  };
}
